import { ApiRequestError, executeSdkRawRequest } from './api-request.util';
import { resolveAccessToken, subscriptionsApi } from './sdk-client';

export type SubscriptionTier = 'free' | 'pro' | 'champion';

export type GracePeriodType = 'free_trial' | 'pro_over_limit';

export interface GracePeriod {
  active: boolean;
  endsAt: string;
  type: GracePeriodType;
}

export type CheckoutPlanCode = 'champion_yearly';

export interface SubscriptionEntitlements {
  sportsLimit: number;
  swapsLimit: number;
  swapsUsed: number;
  swapsRemaining: number;
  freeSwapUsed: boolean;
}

export interface LaunchPromo {
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  introPriceAmount: number;
  listPriceAmount: number;
  offeringId: string;
}

export interface PlanCatalogItem {
  code: string;
  displayName: string;
  tier: string;
  billingCycle: string;
  isMostPopular: boolean;
  appleProductId: string | null;
  googlePlayProductId: string | null;
  price: { amount: string; currency: string };
}

export interface PlanCatalog {
  items: PlanCatalogItem[];
  launchPromo: LaunchPromo;
}

export interface MySubscription {
  tier: SubscriptionTier;
  planCode: string;
  status: string;
  autoRenew: boolean;
  expiresAt: string | null;
  trialEndsAt?: string | null;
  /** Start of the current plan/billing period. */
  currentPeriodStart?: string | null;
  entitlements: SubscriptionEntitlements;
  selectedSportCodes: string[];
  gracePeriod?: GracePeriod;
  provider?: string;
  store?: 'APP_STORE' | 'PLAY_STORE' | 'PROMOTIONAL' | 'MAC_APP_STORE' | 'UNKNOWN' | null;
}

export interface CheckoutParams {
  planCode: CheckoutPlanCode;
  selectedSportCodes?: string[];
  idempotencyKey?: string;
}

export interface ReorderSportOption {
  code: string;
  name: string;
}

export interface AccessibleSportsResponse {
  sportCodes: string[];
  reorderSports: ReorderSportOption[];
}

export interface SwapSportParams {
  fromSportCode: string;
  toSportCode: string;
}

export interface SwapSportResponse {
  sportCodes: string[];
  swapsRemaining: number;
}

export interface ReorderSportsResponse {
  sportCodes: string[];
  swapsRemaining: number;
  swapsUsed: number;
  freeSwapUsed: boolean;
}

export interface PaymentHistoryItem {
  id: string;
  provider: string;
  status: string;
  amount: string;
  currency: string;
  /** Plan tier billed for this charge — not the user's current subscription. */
  tier: SubscriptionTier | null;
  planCode: string | null;
  paidAt: string | null;
  createdAt: string;
}

/**
 * OpenAPI `createFetchParams` shallow-merges initOverrides over the request.
 * Returning `{ headers: { Authorization } }` alone replaces the whole headers
 * object and drops `Content-Type`, so Nest never parses the JSON body (400
 * "sportCodes must be an array" / "RequestBody: no body").
 * Use an override fn that merges into the existing init instead.
 */
function authInit(
  accessToken?: string | null,
): ((args: { init: RequestInit }) => Promise<RequestInit>) | undefined {
  const token = accessToken ?? resolveAccessToken();
  if (!token) {
    return undefined;
  }

  return async ({ init }) => ({
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${token}`,
    },
  });
}

function normalizeReorderSports(value: unknown): ReorderSportOption[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: ReorderSportOption[] = [];
  for (const item of value) {
    if (item && typeof item === 'object' && 'code' in item) {
      const row = item as { code: unknown; name?: unknown };
      const code = typeof row.code === 'string' ? row.code : null;
      if (!code) {
        continue;
      }
      result.push({
        code,
        name: typeof row.name === 'string' ? row.name : code,
      });
    }
  }
  return result;
}

function normalizeAccessibleSports(payload: unknown): AccessibleSportsResponse {
  const data = (payload ?? {}) as {
    sportCodes?: unknown;
    reorderSports?: unknown;
  };

  return {
    sportCodes: Array.isArray(data.sportCodes)
      ? data.sportCodes.filter((code): code is string => typeof code === 'string')
      : [],
    reorderSports: normalizeReorderSports(data.reorderSports),
  };
}

function normalizeMySubscription(payload: unknown): MySubscription {
  const data = (payload ?? {}) as MySubscription;
  const store = data.store;
  return {
    ...data,
    store:
      store === 'APP_STORE' ||
      store === 'PLAY_STORE' ||
      store === 'PROMOTIONAL' ||
      store === 'MAC_APP_STORE' ||
      store === 'UNKNOWN'
        ? store
        : (data.store ?? null),
    ...(typeof data.provider === 'string' ? { provider: data.provider } : {}),
  };
}

function normalizeReorderSportsResponse(payload: unknown): ReorderSportsResponse {
  const data = (payload ?? {}) as Partial<ReorderSportsResponse>;
  return {
    sportCodes: Array.isArray(data.sportCodes) ? data.sportCodes : [],
    swapsRemaining: typeof data.swapsRemaining === 'number' ? data.swapsRemaining : 0,
    swapsUsed: typeof data.swapsUsed === 'number' ? data.swapsUsed : 0,
    freeSwapUsed: Boolean(data.freeSwapUsed),
  };
}

function normalizeSwapSportResponse(payload: unknown): SwapSportResponse {
  const data = (payload ?? {}) as Partial<SwapSportResponse>;
  return {
    sportCodes: Array.isArray(data.sportCodes) ? data.sportCodes : [],
    swapsRemaining: typeof data.swapsRemaining === 'number' ? data.swapsRemaining : 0,
  };
}

function normalizeSubscriptionTier(
  value: unknown,
): SubscriptionTier | null {
  if (value === 'free' || value === 'pro' || value === 'champion') {
    return value;
  }
  return null;
}

function normalizePaymentHistoryItem(item: unknown): PaymentHistoryItem | null {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const raw = item as Record<string, unknown>;
  if (typeof raw.id !== 'string') {
    return null;
  }

  return {
    id: raw.id,
    provider: typeof raw.provider === 'string' ? raw.provider : '',
    status: typeof raw.status === 'string' ? raw.status : '',
    amount: typeof raw.amount === 'string' ? raw.amount : String(raw.amount ?? ''),
    currency: typeof raw.currency === 'string' ? raw.currency : '',
    tier: normalizeSubscriptionTier(raw.tier),
    planCode: typeof raw.planCode === 'string' ? raw.planCode : null,
    paidAt: typeof raw.paidAt === 'string' ? raw.paidAt : null,
    createdAt:
      typeof raw.createdAt === 'string' ? raw.createdAt : new Date(0).toISOString(),
  };
}

function normalizePaymentHistory(payload: unknown): PaymentHistoryItem[] {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload
    .map(normalizePaymentHistoryItem)
    .filter((item): item is PaymentHistoryItem => item !== null);
}

/** Preserve status in Error.message so callers that scan for "409" still work. */
function rethrowSubscriptionError(error: unknown, fallback: string): never {
  if (error instanceof ApiRequestError) {
    const message = error.message.includes(String(error.status))
      ? error.message
      : `${error.message} (${error.status})`;
    const wrapped = new Error(message) as Error & { errorCode?: string };
    if (error.errorCode) {
      wrapped.errorCode = error.errorCode;
    }
    throw wrapped;
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new Error(fallback);
}

function normalizeLaunchPromo(value: unknown): LaunchPromo {
  const raw = (value ?? {}) as Partial<LaunchPromo>;
  return {
    isActive: Boolean(raw.isActive),
    startsAt: typeof raw.startsAt === 'string' ? raw.startsAt : null,
    endsAt: typeof raw.endsAt === 'string' ? raw.endsAt : null,
    introPriceAmount:
      typeof raw.introPriceAmount === 'number' ? raw.introPriceAmount : 299,
    listPriceAmount:
      typeof raw.listPriceAmount === 'number' ? raw.listPriceAmount : 299,
    offeringId: typeof raw.offeringId === 'string' ? raw.offeringId : 'launch',
  };
}

function normalizePlanCatalog(payload: unknown): PlanCatalog {
  if (Array.isArray(payload)) {
    return {
      items: payload as PlanCatalogItem[],
      launchPromo: normalizeLaunchPromo(null),
    };
  }

  const data = (payload ?? {}) as { items?: unknown; launchPromo?: unknown };
  return {
    items: Array.isArray(data.items) ? (data.items as PlanCatalogItem[]) : [],
    launchPromo: normalizeLaunchPromo(data.launchPromo),
  };
}

export async function getSubscriptionPlans(
  countryId?: string,
): Promise<PlanCatalog> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerListPlansV1Raw(
          countryId !== undefined ? { countryId } : {},
        ),
      fallback: 'Subscription plans API failed',
    });
    return normalizePlanCatalog(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription plans API failed');
  }
}

export async function syncSubscription(
  accessToken?: string | null,
): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerSyncV1Raw(authInit(accessToken)),
      fallback: 'Subscription sync failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription sync failed');
  }
}

export async function restoreSubscription(
  accessToken?: string | null,
): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerRestoreV1Raw(
          authInit(accessToken),
        ),
      fallback: 'Restore purchases failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Restore purchases failed');
  }
}

export async function confirmSubscriptionPurchase(
  params: CheckoutParams,
): Promise<{ pending: boolean; subscription: MySubscription | null }> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerConfirmV1Raw(
          {
            checkoutSubscriptionDto: {
              planCode: params.planCode,
              ...(params.selectedSportCodes !== undefined
                ? { selectedSportCodes: params.selectedSportCodes }
                : {}),
              ...(params.idempotencyKey !== undefined
                ? { idempotencyKey: params.idempotencyKey }
                : {}),
            },
          },
          authInit(),
        ),
      fallback: 'Confirm purchase failed',
    });

    if (data && typeof data === 'object' && 'pending' in data) {
      const pending = data as {
        pending?: boolean;
        subscription?: unknown;
      };
      return {
        pending: Boolean(pending.pending),
        subscription: pending.subscription
          ? normalizeMySubscription(pending.subscription)
          : null,
      };
    }

    return {
      pending: false,
      subscription: normalizeMySubscription(data),
    };
  } catch (error) {
    rethrowSubscriptionError(error, 'Confirm purchase failed');
  }
}

export async function getMySubscription(
  accessToken?: string | null,
): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerGetMySubscriptionV1Raw(
          authInit(accessToken),
        ),
      fallback: 'Subscription API failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription API failed');
  }
}

export async function getAccessibleSports(
  accessToken?: string | null,
): Promise<AccessibleSportsResponse> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerGetAccessibleSportsV1Raw(
          authInit(accessToken),
        ),
      fallback: 'Subscription sports API failed',
    });
    return normalizeAccessibleSports(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription sports API failed');
  }
}

export async function getPaymentHistory(
  accessToken?: string | null,
): Promise<PaymentHistoryItem[]> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerGetPaymentHistoryV1Raw(
          authInit(accessToken),
        ),
      fallback: 'Subscription payments API failed',
    });
    return normalizePaymentHistory(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription payments API failed');
  }
}

export async function checkoutSubscription(
  params: CheckoutParams,
): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerCheckoutV1Raw(
          {
            checkoutSubscriptionDto: {
              planCode: params.planCode,
              ...(params.selectedSportCodes !== undefined
                ? { selectedSportCodes: params.selectedSportCodes }
                : {}),
              ...(params.idempotencyKey !== undefined
                ? { idempotencyKey: params.idempotencyKey }
                : {}),
            },
          },
          authInit(),
        ),
      fallback: 'Subscription checkout failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Subscription checkout failed');
  }
}

export async function startFreeTrial(): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerStartFreeTrialV1Raw(authInit()),
      fallback: 'Start free trial failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Start free trial failed');
  }
}

export async function cancelSubscription(): Promise<MySubscription> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerCancelV1Raw(authInit()),
      fallback: 'Cancel subscription failed',
    });
    return normalizeMySubscription(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Cancel subscription failed');
  }
}

export async function swapSport(params: SwapSportParams): Promise<SwapSportResponse> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerSwapSportV1Raw(
          {
            swapSportDto: {
              fromSportCode: params.fromSportCode,
              toSportCode: params.toSportCode,
            },
          },
          authInit(),
        ),
      fallback: 'Swap sport failed',
    });
    return normalizeSwapSportResponse(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Swap sport failed');
  }
}

export async function reorderSports(
  sportCodes: string[],
): Promise<ReorderSportsResponse> {
  try {
    const data = await executeSdkRawRequest<unknown>({
      request: () =>
        subscriptionsApi.subscriptionsControllerReorderSportsV1Raw(
          { reorderSportsDto: { sportCodes } },
          authInit(),
        ),
      fallback: 'Reorder sports failed',
    });
    return normalizeReorderSportsResponse(data);
  } catch (error) {
    rethrowSubscriptionError(error, 'Reorder sports failed');
  }
}

export function checkoutPlanCodeFromUserPlan(
  plan: 'champion',
): CheckoutPlanCode {
  if (plan !== 'champion') {
    throw new Error('Only Champion is available for checkout');
  }
  return 'champion_yearly';
}
